/**
 * The hold library (lane C0-POSE-FIX): how a drawn weapon is carried at ease, standing and walking. Lane C0-WEAPON
 * gave the fighting stance its grip (core.ts `readyBlade`); outside it the arm used to hang straight with the blade
 * dropped along the leg, and with the weapon drawn by default that was the hold seen nearly all the time.
 *
 * Each hold is data (`HOLDS`): which hand holds the weapon, where that hand goes and at what angle the weapon lies,
 * how much the walk swings it, the weapon kinds it suits, and the research behind it (docs/research/C0_POSES.md
 * "Weapon holds"). A figure carries the hold its look names (`Look.hold`, declared here) or its weapon kind's default
 * (`DEFAULT_HOLD`); switching a hold needs no code change. The skeleton (rig/skeleton.ts) lays the hold over the
 * hanging arms of the standing, walking and stance key poses (`applyHold`); the fighting poses keep their own arms.
 *
 * A hold in the off hand (`hand: 'B'`, e.g. the taiji sword's reverse grip along the left forearm) uses the far arm
 * `revArm` (rig/weapons.ts draws the weapon in that hand, rig/hands.ts closes it round the grip).
 */
import type { FarArm, NearArm, Rig } from './kit';
import type { Pose } from './index';
import { approved } from './review';

/** Straight-limb weapon-arm positions the holds use (classic design coordinates: hand, blade angle, in front). */
export const ARMS = {
  /** Carried at ease: elbow bent, hand ahead of the hip, the blade tipped up along the forearm. */
  carryBlade: { hand: [5, 35] as [number, number], ang: -1.3, front: true },
  /** Trailing behind: the hand a little behind the hip, the blade back and down behind the body. */
  trailBlade: { hand: [-3, 36] as [number, number], ang: Math.PI - 0.75, front: false },
  /** Reverse grip behind the back: the hand at the small of the back, the blade up along the spine. */
  reverseUp: { hand: [-7, 31] as [number, number], ang: -Math.PI / 2 - 0.08, front: false },
  /** On the shoulder: the hand at the chest, the blade laid back over the shoulder, clear of the face. */
  shoulderRest: { hand: [4, 31] as [number, number], ang: -Math.PI + 0.95, front: false },
  /** Presented low in front, point level ahead (the off hand's sword fingers beside it). */
  presentLow: { hand: [8, 33] as [number, number], ang: -0.1, front: true },
  /** A staff or spear stood upright beside the body, the hand low by the hip, the butt planted on the ground. */
  staffUpright: { hand: [6, 41] as [number, number], ang: -Math.PI / 2, front: true },
  /** A staff or spear over the shoulder, sloping back low behind the head (clear of the chin). */
  staffShoulder: { hand: [4, 33] as [number, number], ang: -Math.PI + 0.35, front: false },
  /** A closed fan held before the chest, upright. */
  fanChest: { hand: [7, 28] as [number, number], ang: -1.25, front: true },
};

/** Where a hold puts the weapon on the screen at ease and on the walk. */
export type HoldContext = 'ease' | 'walk' | 'run' | 'ready' | 'guard' | 'mounted';

export interface HoldDef {
  label: string;
  /** Weapon kinds it suits (rig/weapons.ts `WeaponKind`). */
  kinds: readonly string[];
  /** Contexts it covers now (the run, the fight and riding keep their own arms for now). */
  contexts: readonly HoldContext[];
  /** The hand that holds the weapon. */
  hand: 'A' | 'B';
  /** The weapon arm (`hand: 'A'`), or the off arm (`hand: 'B'`); the other arm keeps the pose's own. */
  arm?: NearArm;
  far?: FarArm;
  /** How much of the walk's arm swing the weapon hand keeps (0: held still). */
  swing: number;
  note: string;
  refs: readonly string[];
  /** Dropped after review (kept for the record; not listed in the studio). */
  dropped?: boolean;
}

const BLADES = ['jian', 'dao', 'kris'] as const;
const LONG = ['staff', 'spear', 'qiang', 'glaive', 'halberd'] as const;

export const HOLDS = {
  revArm: {
    label: 'กลับด้ามแนบแขนซ้าย (左手反贴臂)',
    kinds: BLADES,
    contexts: ['ease', 'walk'],
    hand: 'B',
    far: 'revArm',
    swing: 0.2,
    note: "Left hand holds the sword reversed, the blade lying up the outside of the left forearm, point by the shoulder; the right hand hangs free. The taiji/Wudang sword's preparation posture; the at-ease hold of wuxia MMOs.",
    refs: ['https://www.xiulong.it/wushu/Core/wudang-sword-notes-2/'],
  },
  reverseUp: {
    label: 'กลับด้ามไว้หลัง ปลายชี้ขึ้น (背剑)',
    kinds: BLADES,
    contexts: ['ease', 'walk'],
    hand: 'A',
    arm: 'reverseUp',
    swing: 0,
    note: 'Right hand at the small of the back, reverse grip, blade up along the spine past the shoulder. The user found it pointing at the sky (2026-10-10).',
    refs: [],
    dropped: true,
  },
  pointDown: {
    label: 'ห้อยปลายลงข้างตัว',
    kinds: [...BLADES, ...LONG],
    contexts: ['ease', 'walk'],
    hand: 'A',
    arm: 'hang',
    swing: 1,
    note: 'The old hold: arm hanging, the point down and a little forward beside the leg, swinging with the walk.',
    refs: ['docs/research/C0_WEAPON.md'],
  },
  carryBlade: {
    label: 'ถือหน้า ข้อศอกงอ',
    kinds: BLADES,
    contexts: ['ease', 'walk'],
    hand: 'A',
    arm: 'carryBlade',
    swing: 0.35,
    note: 'Elbow bent, hand ahead of the hip, the blade tipped up along the forearm (R2: was level and stuck out like a pole; R3: steeper and the hand lower, so blade and scabbard no longer open into a V in the three-quarter views).',
    refs: ['docs/research/C0_WEAPON.md'],
  },
  trailBlade: {
    label: 'ลากปลายไปข้างหลัง',
    kinds: BLADES,
    contexts: ['ease', 'walk'],
    hand: 'A',
    arm: 'trailBlade',
    swing: 0.35,
    note: 'Hand a little behind the hip, the blade trailing back and down, as the qinggong run carries it. Dropped (R1: sloppy, not a trained bearing).',
    refs: [],
    dropped: true,
  },
  shoulderRest: {
    label: 'พาดบ่า',
    kinds: ['dao', ...BLADES.filter((k) => k !== 'dao')],
    contexts: ['ease', 'walk'],
    hand: 'A',
    arm: 'shoulderRest',
    swing: 0,
    note: 'The blade laid back over the shoulder, the hand at the chest so the face stays clear (R2): a sabre-man at ease. Dropped (R2: the blade hides behind the body and head in most views; a lazy bandit read).',
    refs: [],
    dropped: true,
  },
  presentLow: {
    label: 'ถือต่ำข้างหน้า ชี้นิ้วกระบี่',
    kinds: BLADES,
    contexts: ['ease'],
    hand: 'A',
    arm: 'presentLow',
    far: 'fwd',
    swing: 0.2,
    note: 'Held low in front, the point level ahead, the off hand in sword fingers beside it. Dropped (R1: reads as a bar; the ready stance already covers it).',
    refs: ['docs/research/POSING_RIGGING.md'],
    dropped: true,
  },
  staffUpright: {
    label: 'ตั้งไม้ข้างตัว',
    kinds: LONG,
    contexts: ['ease'],
    hand: 'A',
    arm: 'staffUpright',
    swing: 0,
    note: 'The staff stood upright beside the body, held low by the hip so its butt is planted on the ground (R2: was short and floating).',
    refs: [],
  },
  staffShoulder: {
    label: 'แบกไม้บนบ่า',
    kinds: LONG,
    contexts: ['ease', 'walk'],
    hand: 'A',
    arm: 'staffShoulder',
    swing: 0,
    note: 'The staff carried over the shoulder, sloping back low behind the head (R2: was across the chin): walking a road. Dropped (R2: reads as a staff held level at the hip, not over the shoulder).',
    refs: [],
    dropped: true,
  },
  fanChest: {
    label: 'ถือพัดหน้าอก',
    kinds: ['fan'],
    contexts: ['ease', 'walk'],
    hand: 'A',
    arm: 'fanChest',
    swing: 0,
    note: 'A closed fan held upright before the chest: the scholar-swordsman. Blocked: needs a fan as Look.weapon (looks/types.ts).',
    refs: [],
  },
} as const satisfies Record<string, HoldDef>;
export type HoldId = keyof typeof HOLDS;
export const HOLD_IDS = Object.keys(HOLDS) as HoldId[];

/**
 * Each weapon kind's proposed hold when its look names none; it is used in play only once approved in the studio's
 * review (`holdFor`). revArm (the taiji preparation hold) is proposed for the blades; reverseUp was rejected.
 */
export const DEFAULT_HOLD: Partial<Record<string, HoldId>> = {
  jian: 'revArm',
  dao: 'revArm',
  kris: 'revArm',
};

declare module '../looks/types' {
  interface Look {
    /** How this figure carries its drawn weapon at ease (motions/hold.ts); left out, its kind's default. */
    hold?: HoldId;
  }
}

/** The hold a figure with weapon kind `kind` drawn carries: its look's own if it suits the kind, else the default. */
export function holdFor(own: HoldId | undefined, kind: string): HoldId | undefined {
  const pick = own && (HOLDS[own].kinds as readonly string[]).includes(kind) ? own : DEFAULT_HOLD[kind];
  // In play only once the user has approved it in the studio's review (motions/review.ts); until then the old hold.
  return pick && approved(`hold:${pick}`) ? pick : undefined;
}

let forced: HoldId | null = null;
/** Review tools only (tools/poseFix, the studio): draw every armed figure with this hold (null: their own). */
export function setHoldForReview(h: HoldId | null): void {
  forced = h;
}

/** Poses that stand or walk at ease (a stance's key poses too, `stanceKey`). */
const AT_EASE = new Set<string>(['idle', 'walk', 'stepA', 'stepB']);

/**
 * Lays hold `id` over a standing, walking or stance key pose whose arms hang (`stanceKey`: the pose is a stance's
 * key). Returns whether it did. A hold in the off hand needs that hand free (not on the hip or the hilt).
 */
export function applyHold(
  pose: Pose,
  rig: Rig,
  stanceKey: boolean,
  id: HoldId | undefined,
  offBusy: boolean,
): boolean {
  const h: HoldDef | undefined = (forced && HOLDS[forced]) || (id && HOLDS[id]) || undefined;
  if (!h || rig.arm !== 'hang' || !(stanceKey || AT_EASE.has(pose))) return false;
  if (h.hand === 'B') {
    if (offBusy || rig.far !== 'hang') return false;
    rig.far = h.far!;
    // The free right hand keeps the walk's swing; the left, holding the blade, barely moves.
    return true;
  }
  if (h.arm) rig.arm = h.arm;
  if (h.far && rig.far === 'hang') rig.far = h.far;
  rig.armSwing *= h.swing;
  return true;
}
